import LedgerDetailModal from '@app/components/ledger/detail/LedgerDetailModal';
import { OrderModel } from '@app/interfaces/order';
import { formatDateTime, formatMoney } from '@app/utils/utils';
import { Space, Button, message, Modal } from 'antd';
import React, { useState } from 'react';
import PaymentForm from '../PaymentForm';
import { MdOutlinePayment } from 'react-icons/md';
import { createLedger, deleteLedger } from '@app/store/slices/ledgerSlice';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import { GrRevert } from 'react-icons/gr';
import { getOrder } from '@app/store/slices/orderSlice';
import { usePermissions } from '@app/hooks/usePermissions';

interface OrderInfoProps {
  record: OrderModel;
}

const PaymentTransaction: React.FC<OrderInfoProps> = ({ record }) => {
  const [showLedgerModal, setShowLedgerModal] = useState(false);
  const [ledgerId, setLedgerId] = useState<number | null>(null);
  const [showLedgerForm, setShowLedgerForm] = useState(false);

  const ledgers = record.ledgers || [];
  const dispatch = useAppDispatch();

  const { allow_delete_payment } = record?.permission as any;
  // Xem/tạo phiếu thu gọi API sổ cái → cần quyền ledgers tương ứng, không thì 403.
  const { can } = usePermissions();
  const canViewLedger = can('ledgers', 'show');
  const canCreateLedger = can('ledgers', 'create');

  const handleViewLedger = (ledger: any) => {
    setShowLedgerModal(true);
    setLedgerId(ledger.id);
  };

  const handleCancel = () => {
    setShowLedgerModal(false);
  };

  const handleCreatePayment = () => {
    setShowLedgerForm(true);
  };

  const fetchOrderData = (orderId: string | undefined, dispatch: any) => {
    if (orderId) {
      dispatch(getOrder(Number(orderId)));
    }
  };

  const onCreatePayment = async (values: any) => {
    console.log('Received values of form: ', values);

    try {
      const response = await dispatch(createLedger(values)).unwrap();

      if (response.data !== undefined) {
        setShowLedgerForm(false);
        fetchOrderData(record.id, dispatch);
      }
    } catch (error: any) {
      message.error(error.message || 'An unknown error occurred');
    }
  };

  const deletePayment = async (ledgerId: number) => {
    try {
      await dispatch(deleteLedger(ledgerId)).unwrap();
      fetchOrderData(record.id, dispatch);
    } catch (error: any) {
      message.error(error.message || 'An unknown error occurred');
    }
  };

  const handleRevertPayment = (ledgerId: number) => {
    Modal.confirm({
      title: 'Xác nhận!',
      content: 'Bạn có chắc xoá thanh toán này?',
      onOk() {
        deletePayment(ledgerId);
      },
    });
  };

  const isCustomerDebit = record.debit_amount != 0 || false;

  return (
    <>
      <div className="p-3">
        <div className="mb-2 font-bold-weight text-right">
          Tổng số tiền chưa thanh toán:{' '}
          <span className="text-danger">{formatMoney(record.debit_amount)}</span>
        </div>
        <table className="payment-table">
          <thead>
            <th>Mã phiếu</th>
            <th>Thời gian</th>
            <th>Giá trị phiếu</th>
            <th>Tiền thu/chi</th>
            <th>Phương thức</th>
            <th>Trạng thái</th>
            <th>Người tạo</th>
            <th></th>
          </thead>
          <tbody>
            {ledgers && ledgers.length > 0 ? (
              ledgers.map((ledger: any) => (
                <tr key={ledger.id}>
                  {' '}
                  {/* Assuming each ledger has a unique id */}
                  {canViewLedger ? (
                    <td className="text-center text-primary pointer" onClick={() => handleViewLedger(ledger)}>
                      {ledger.code}
                    </td>
                  ) : (
                    <td className="text-center">{ledger.code}</td>
                  )}
                  <td className="text-center">{formatDateTime(ledger.trans_date, 'date')}</td>
                  <td className="text-right text-primary">{formatMoney(ledger.order_amount)}</td>
                  <td className="text-right text-primary">{formatMoney(ledger.amount)}</td>
                  <td className="text-center">{ledger.payment_type}</td>
                  <td className="text-center text-primary">{ledger.posted ? 'Đã thanh toán' : 'Chưa thanh toán'}</td>
                  <td className="text-center">{ledger.created_by}</td>
                  <td>
                    {allow_delete_payment && (
                      <Button type="dashed" onClick={() => handleRevertPayment(ledger.id)}>
                        <GrRevert /> Revert
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="text-center">
                  No data.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex-center p-4">
          <Space>
            {isCustomerDebit && canCreateLedger && (
              <Button type="primary" onClick={handleCreatePayment}>
                <MdOutlinePayment /> Tạo phiếu thanh toán
              </Button>
            )}
          </Space>
        </div>
      </div>

      {showLedgerModal && (
        <LedgerDetailModal
          open={showLedgerModal}
          onCancel={handleCancel}
          recordId={ledgerId ?? undefined}
          title="Chi tiết phiếu thu"
        />
      )}

      {showLedgerForm && (
        <PaymentForm
          open={showLedgerForm}
          onCreate={onCreatePayment}
          onCancel={() => setShowLedgerForm(false)}
          title="Tạo phiếu thanh toán"
          record={record}
          type="receivable"
        />
      )}

    </>
  );
};

export default PaymentTransaction;
